import ChatLock from '@assets/images/common/icon/channel/lock.svg';
import { Channel } from '@src/api/shared/community/channelApiClient';
import { brandColors } from '@src/assets/colors/brandColors';
import { ChannelName } from '@src/components/ui/ChannelName';
import { ChannelVisibility } from '@src/features/shared/community/enums/channelVisibility';
import { SHARED_URL } from '@src/routes/shared';
import { setPreservedUnreadLine } from '@src/store/channelSlice';
import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { generatePath } from 'react-router-dom';

interface ChannelItemProps {
  channel: Channel;
  isCurrent: boolean;
  handleLinkClick: (e: React.MouseEvent<HTMLElement>, to: string, channelCode?: string) => void;
  badgeBaseClasses: string;
  badgeCustomStyle: React.CSSProperties;
}

const ChannelItem = React.memo<ChannelItemProps>(
  ({ channel, isCurrent, handleLinkClick, badgeBaseClasses, badgeCustomStyle }) => {
    const dispatch = useDispatch();
    const hasUnread = channel.has_unread;
    const hasMention = channel.has_mention;
    const mentionCount = channel.mention_count;

    const targetUrl = useMemo(() => {
      return `${generatePath(SHARED_URL.COMMUNITY_CHANNEL, { code: channel.code })}${
        channel.first_unread_chat_id ? `?chatId=${channel.first_unread_chat_id}` : ''
      }`;
    }, [channel.code, channel.first_unread_chat_id]);

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        if (channel.first_unread_chat_id && !isCurrent) {
          dispatch(
            setPreservedUnreadLine({
              channelCode: channel.code,
              chatId: channel.first_unread_chat_id,
            }),
          );
        }

        handleLinkClick(e, targetUrl, channel.code);
      },
      [handleLinkClick, targetUrl, channel.code, channel.first_unread_chat_id, isCurrent, dispatch],
    );

    const linkClassName = useMemo(() => {
      return isCurrent
        ? 'flex w-full items-center border-b-2 border-primary-500 pb-2 cursor-pointer'
        : 'flex w-full items-center pb-2 cursor-pointer';
    }, [isCurrent]);

    const iconColor = useMemo(() => {
      return isCurrent || hasMention || hasUnread ? brandColors['500'] : '#D9D9D9';
    }, [isCurrent, hasMention, hasUnread]);

    const textClassName = useMemo(() => {
      return `w-fit flex-1 truncate text-sm ${
        isCurrent
          ? 'font-bold text-primary-500'
          : hasMention || hasUnread
            ? 'font-bold text-gray-700'
            : 'font-normal text-gray-700'
      }`;
    }, [isCurrent, hasMention, hasUnread]);

    return (
      <li>
        <div
          className={linkClassName}
          onClick={handleClick}
        >
          <div
            className='flex flex-1 items-center gap-2 truncate pb-2'
            title={channel.name}
          >
            <ChannelName
              name={channel.name}
              emoji={channel.emoji}
              iconColor={iconColor}
              textClassName={textClassName.replace('w-fit flex-1 truncate text-sm', '')}
              iconSize={18}
              visibility={channel.visibility}
              showLockIcon={false}
            />
          </div>
          <div className='flex items-center gap-1'>
            {(channel.visibility === ChannelVisibility.LIMITED ||
              channel.visibility === ChannelVisibility.PRIVATE) && (
              <img
                className='size-4'
                src={ChatLock}
                alt=''
              />
            )}

            {hasMention && (
              <span
                className={badgeBaseClasses}
                style={badgeCustomStyle}
              >
                {mentionCount}
              </span>
            )}
          </div>
        </div>
      </li>
    );
  },
);

ChannelItem.displayName = 'ChannelItem';

export default ChannelItem;
